import {
  useState
} from 'react'
import {
  Check, Loader2, X
} from 'lucide-react'
import type {
  IntegrationSuggestionItem
} from '../services/api'

// Monogram colouring, now a *fallback* rather than the tile's identity: a
// suggestion carries the brand's own domain (`logo_domain`), so the usual case
// is the real mark. This still runs whenever we have no domain (a retired
// catalog entry) or the logo host 404s. Hashing the name keeps a given app the
// same colour everywhere it appears.
const MONOGRAM_COLORS = [
  'bg-rose-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-sky-500',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-fuchsia-500',
]

function monogramColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  }
  // The modulo guarantees an in-range index, but noUncheckedIndexedAccess
  // widens the lookup to `| undefined` regardless — so fall back rather than
  // assert, which keeps the invariant honest if the palette is ever emptied.
  return MONOGRAM_COLORS[hash % MONOGRAM_COLORS.length] ?? 'bg-gray-500'
}

// Three sources, best first, because no single one covers the catalog.
//
// 1. `logo_url` — the brand's real asset, resolved during the background scan
//    from Pipedream's catalog or the promoted registry. This is the usual case
//    and the only one that is an actual logo rather than an icon.
// 2. The brand's domain, via a favicon service. In-house apps have no
//    `logo_url` (their marks are ours to bundle, not to fetch), and a lookup
//    can fail, so this keeps those tiles recognisable.
// 3. The monogram, below.
//
// DuckDuckGo rather than Google's `s2/favicons` for step 2: s2 answers 200 with
// a generic globe for a domain it doesn't know, which would defeat the monogram
// fallback by never firing `onError`. This one 404s, so an unknown brand
// degrades to its letter instead of to someone else's placeholder.
//
// KNOWN GAP: step 2 tells a third party which apps we detected in this user's
// inbox, on the one screen that promises we read "sender addresses only". It
// covers the 11 in-house apps until their bundled SVGs are wired up, at which
// point this should go.
function faviconUrl(domain: string): string {
  return `https://icons.duckduckgo.com/ip3/${encodeURIComponent(domain)}.ico`
}

interface SuggestedIntegrationTileProps {
  suggestion: IntegrationSuggestionItem
  loading?: boolean | undefined
  // Already connected — the tile becomes a confirmation, not an action. Both
  // surfaces re-read server state after a connect completes, so this reflects
  // what actually persisted rather than what the click hoped for.
  connected?: boolean | undefined
  // The tile's primary action. Both surfaces now pass the connect flow: the
  // onboarding step's whole job is letting someone pick up the tools they
  // already use, and a grid you can only subtract from cannot do that.
  onClick?: (() => void) | undefined
  // When provided, a dismiss affordance renders in the corner. Onboarding
  // deliberately passes nothing: with connecting as the primary action there
  // is no longer any reason to make a new user curate a list before they can
  // move on, and dismissal survives every rescan, so a stray tap there is
  // unrecoverable. The integrations page keeps it — that is a considered
  // "never show me this again", made at leisure.
  onDismiss?: (() => void) | undefined
  // What the inert state tells a screen reader, after the app's name. Defaults
  // to the onboarding answer, where "in Settings" is a real destination the
  // step's own subtitle promises.
  //
  // A prop because the honest answer is surface-dependent: this tile also
  // mounts ON the Settings page, where the default would point a screen-reader
  // user at the page they are already on — and, since GET-12059 hid the
  // "Connect any app" catalog outside the internal cohort, often to a card
  // that isn't rendered. Rare there (see `SuggestedIntegrationsCard`) but
  // wrong when it happens, and the default stays right for onboarding.
  inertHint?: string | undefined
}

/**
 * One suggested integration: brand mark and name, nothing else.
 *
 * Deliberately shows no evidence ("47 emails in 90 days"). Naming a number
 * invites the user to audit our reasoning at the exact moment we want them
 * choosing tools, and it exposes that we counted their mail — true, and
 * disclosed in the step's subtitle, but not something every tile should repeat.
 * Matches `DynamicIntegrationTile` so the two surfaces read as one system.
 */
export function SuggestedIntegrationTile({
  suggestion,
  loading = false,
  connected = false,
  onClick,
  onDismiss,
  inertHint = 'connect in Settings',
}: SuggestedIntegrationTileProps): JSX.Element {
  const [logoFailed, setLogoFailed] = useState(false)
  const interactive = onClick !== undefined && !connected
  const domain = suggestion.logo_domain
  // One `onError` retires the whole chain rather than stepping to the next
  // source: a per-source cursor would re-request on every render as the state
  // changed, and the monogram is a fine answer for the rare app whose real
  // asset 404s.
  const src = suggestion.logo_url ?? (domain != null ? faviconUrl(domain) : null)
  const showLogo = src !== null && !logoFailed

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        disabled={loading || !interactive}
        // Three states, not two: a tile with no `onClick` is inert (a
        // discovered-OAuth app, or a Pipedream one for a user outside that
        // cohort), and announcing "Connect X" on a button that does nothing is
        // the one case a screen reader is actively misled by.
        aria-label={
          connected
            ? `${suggestion.display_name} connected`
            : interactive
              ? `Connect ${suggestion.display_name}`
              : `${suggestion.display_name} — ${inertHint}`
        }
        className={`relative w-full flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-colors ${
          connected
            ? 'bg-green-50 border-green-200 cursor-default'
            : 'bg-white border-gray-200'
        } ${
          interactive
            ? 'hover:border-primary-300 hover:shadow-sm cursor-pointer'
            : 'cursor-default'
        } ${loading ? 'opacity-60 cursor-wait' : ''}`}
      >
        {loading ? (
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        ) : showLogo ? (
          <img
            src={src}
            alt=""
            // Decorative: the visible name below already carries the label, so
            // announcing the mark as well would read the app's name twice.
            aria-hidden="true"
            width={32}
            height={32}
            loading="lazy"
            onError={() => setLogoFailed(true)}
            className="w-8 h-8 rounded object-contain"
          />
        ) : (
          <div
            className={`w-8 h-8 rounded flex items-center justify-center text-white text-sm font-semibold ${monogramColor(suggestion.display_name)}`}
          >
            {suggestion.display_name.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-xs text-center font-medium text-gray-700">
          {suggestion.display_name}
        </span>
        {connected && (
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </span>
        )}
      </button>
      {/* Always visible. An earlier revision hid it behind `group-hover`
          whenever the tile had a primary action — i.e. on the integrations
          page, the one surface where the tile IS tappable and therefore the one
          place a touch-only user needs it. That inverted the reasoning it was
          written to follow, so the control is now unconditional wherever it
          renders at all. Suppressed once connected, where "never show me this
          again" no longer has a meaning. */}
      {onDismiss !== undefined && !connected && (
        <button
          type="button"
          onClick={onDismiss}
          // The tile's primary button already goes disabled while a dismissal
          // is in flight; this one didn't, so the tile spun while its X stayed
          // live. Both surfaces leave the tile in place when the DELETE fails
          // and fire their analytics before awaiting it, so an impatient second
          // tap was a second recorded judgement for one decision.
          disabled={loading}
          aria-label={`Not interested in ${suggestion.display_name}`}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:border-gray-300 shadow-sm disabled:opacity-60 disabled:cursor-wait disabled:hover:text-gray-400 disabled:hover:border-gray-200"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  )
}
